import { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

// Ruta relativa a PUBLIC_URL (como el resto de los assets públicos de la app), para que
// funcione tanto si el sitio está publicado en la raíz del dominio como en una subcarpeta.
const MODELS_URL = `${process.env.PUBLIC_URL}/models`;

// face-api.js (~150kB gzip) se importa de forma dinámica para que no infle el bundle
// principal de la app: solo se descarga cuando alguien abre este modal.
let faceapiRef = null;
let modelsLoadPromise = null;
const loadModels = () => {
    if (!modelsLoadPromise) {
        modelsLoadPromise = import('face-api.js').then(async (faceapi) => {
            faceapiRef = faceapi;
            await faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL);
        });
    }
    return modelsLoadPromise;
};

// Ancho del rostro detectado (relativo al ancho del video) a partir del cual se considera "cerca"
const UMBRAL_CERCA = 0.32;
const INTERVALO_DETECCION_MS = 350;

const ESTADO_UI = {
    buscando: { color: 'rgba(255,255,255,0.85)', mensaje: 'Centrá tu rostro dentro del óvalo' },
    lejos:    { color: '#f04438', mensaje: 'Acercate un poco más' },
    cerca:    { color: '#12b76a', mensaje: '¡Perfecto! Mantené la posición' },
};

// Modal para asociar una foto: captura por webcam o subiendo un archivo.
// onCapture recibe un Blob/File listo para mandar como adjunto multipart.
const WebcamCapture = ({ visible, onHide, onCapture, header = 'Tomar foto' }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const streamRef = useRef(null);
    const deteccionRef = useRef(null);
    const detectandoRef = useRef(false);
    const [error, setError] = useState('');
    const [estadoRostro, setEstadoRostro] = useState('buscando');

    useEffect(() => {
        if (!visible) {
            detenerTodo();
            return;
        }
        setError('');
        setEstadoRostro('buscando');

        navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: 'user' } })
            .then((stream) => {
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => {});
                }
                iniciarDeteccion();
            })
            .catch(() => setError('No se pudo acceder a la cámara. Podés subir un archivo en su lugar.'));

        return () => detenerTodo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const detenerTodo = () => {
        if (deteccionRef.current) {
            clearInterval(deteccionRef.current);
            deteccionRef.current = null;
        }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };

    const iniciarDeteccion = async () => {
        try {
            await loadModels();
        } catch (err) {
            // Sin modelo disponible (ej. offline, o /models no servido correctamente):
            // el óvalo queda con el estilo neutro de siempre, pero se deja rastro en consola.
            console.warn('No se pudo cargar el modelo de detección facial (/models):', err);
            return;
        }

        deteccionRef.current = setInterval(async () => {
            const video = videoRef.current;
            if (!video || video.readyState < 2 || detectandoRef.current) return;

            detectandoRef.current = true;
            try {
                const deteccion = await faceapiRef.detectSingleFace(
                    video,
                    new faceapiRef.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 })
                );

                if (!deteccion) {
                    setEstadoRostro('lejos');
                } else {
                    const anchoRelativo = deteccion.box.width / video.videoWidth;
                    setEstadoRostro(anchoRelativo >= UMBRAL_CERCA ? 'cerca' : 'lejos');
                }
            } catch {
                // Ignorar errores puntuales de un frame; se reintenta en el próximo intervalo.
            } finally {
                detectandoRef.current = false;
            }
        }, INTERVALO_DETECCION_MS);
    };

    const capturar = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !video.videoWidth) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);

        canvas.toBlob((blob) => {
            if (blob) onCapture(blob);
        }, 'image/jpeg', 0.9);
    };

    const onFileSelected = (e) => {
        const file = e.target.files?.[0];
        if (file) onCapture(file);
        e.target.value = '';
    };

    const { color: colorOvalo, mensaje: mensajeOvalo } = ESTADO_UI[estadoRostro];

    return (
        <Dialog header={header} visible={visible} style={{ width: '440px' }} modal onHide={onHide}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
                {error ? (
                    <p style={{ color: '#98a2b3', fontSize: '0.9rem', textAlign: 'center' }}>{error}</p>
                ) : (
                    <div style={{ position: 'relative', width: '100%' }}>
                        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: 10, background: '#000', display: 'block' }} />
                        {/* Óvalo guía: referencia visual para centrar y acercar el rostro, no recorta la foto */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <div style={{
                                width: '52%', aspectRatio: '3 / 4', borderRadius: '50%',
                                border: `3px solid ${colorOvalo}`,
                                boxShadow: `0 0 0 1000px rgba(0,0,0,0.35), 0 0 14px 2px ${colorOvalo}`,
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                            }} />
                            <span style={{
                                position: 'absolute', bottom: 10, color: colorOvalo, fontSize: '0.78rem',
                                textShadow: '0 1px 3px rgba(0,0,0,0.8)', fontWeight: 600,
                                transition: 'color 0.2s',
                            }}>
                                {mensajeOvalo}
                            </span>
                        </div>
                    </div>
                )}
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'center' }}>
                    {!error && (
                        <Button label="Capturar" icon="pi pi-camera" onClick={capturar} />
                    )}
                    <Button
                        label="Subir archivo"
                        icon="pi pi-upload"
                        severity="secondary"
                        outlined
                        onClick={() => fileInputRef.current?.click()}
                    />
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onFileSelected}
                />
            </div>
        </Dialog>
    );
};

export default WebcamCapture;
